<div class="card add-edit-offer-card" [style.pointer-events]="isSubmitting ? 'none' : 'auto'"
  [style.opacity]="isSubmitting ? '0.7' : '1'">
  <div class="card-body">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4 pb-3 border-bottom">
      <div>
        <h4 class="mb-1 fw-bold fs-22">
          {{ isAdd ? 'Create New Offer / Lucky Draw' : 'Edit Offer / Lucky Draw' }}
        </h4>
        <p class="text-muted mb-0 fs-14">
          Configure milestone targets, categories, lucky draw eligibility, and promotional rules.
        </p>
      </div>
      <div>
        <button mat-stroked-button color="primary" [routerLink]="['/offer']" class="d-flex align-items-center gap-1">
          <mat-icon>arrow_back</mat-icon>
          <span>Back to Offer List</span>
        </button>
      </div>
    </div>

    <!-- Spinner -->
    <div class="spinner py-5" *ngIf="spinner">
      <mat-spinner diameter="40"></mat-spinner>
    </div>

    <!-- Main Form -->
    <form [formGroup]="offerForm" (ngSubmit)="onSubmit()" *ngIf="!spinner">
      <div class="row">
        <!-- Left Column: Form Details -->
        <div class="col-12 col-lg-8">
          <!-- Section 1: Basic Information -->
          <mat-card class="form-section-card mb-4">
            <mat-card-header class="pb-2">
              <mat-card-title class="fs-16 fw-bold text-primary d-flex align-items-center gap-2">
                <mat-icon>info</mat-icon>
                <span>Basic Offer Information</span>
              </mat-card-title>
            </mat-card-header>
            <mat-card-content class="pt-3">
              <!-- Offer Name -->
              <mat-form-field class="w-100 mb-3" appearance="outline">
                <mat-label>Offer / Milestone Name</mat-label>
                <input matInput formControlName="name" placeholder="e.g. Mile Stone 1, Festival Mega Draw 2026" />
                <mat-error *ngIf="offerForm.controls['name'].hasError('required')">
                  Offer name is required.
                </mat-error>
              </mat-form-field>

              <!-- Display Order -->
              <mat-form-field class="w-100 mb-3" appearance="outline">
                <mat-label>Display Order (Position in Mobile App)</mat-label>
                <input matInput type="number" formControlName="sortOrder" placeholder="e.g. 1, 2, 3" min="0" />
                <mat-icon matPrefix class="text-muted me-1">sort</mat-icon>
                <mat-hint>Lower number = shown first in mobile app carousel. Use 1, 2, 3… for milestone
                  sequence.</mat-hint>
              </mat-form-field>

              <!-- Date Range: Start Date & End Date -->
              <div class="row">
                <div class="col-12 col-md-6 mb-3">
                  <mat-form-field class="w-100" appearance="outline">
                    <mat-label>Start Date</mat-label>
                    <input matInput [matDatepicker]="startPicker" placeholder="Select Start Date"
                      formControlName="startDate" />
                    <mat-datepicker-toggle matIconSuffix [for]="startPicker"></mat-datepicker-toggle>
                    <mat-datepicker #startPicker></mat-datepicker>
                    <mat-error *ngIf="offerForm.controls['startDate'].hasError('required')">
                      Start date is required.
                    </mat-error>
                  </mat-form-field>
                </div>

                <div class="col-12 col-md-6 mb-3">
                  <mat-form-field class="w-100" appearance="outline">
                    <mat-label>End Date</mat-label>
                    <input matInput [matDatepicker]="endPicker" placeholder="Select End Date"
                      formControlName="endDate" />
                    <mat-datepicker-toggle matIconSuffix [for]="endPicker"></mat-datepicker-toggle>
                    <mat-datepicker #endPicker></mat-datepicker>
                    <mat-error *ngIf="offerForm.controls['endDate'].hasError('required')">
                      End date is required.
                    </mat-error>
                  </mat-form-field>
                </div>
              </div>

              <!-- Description / Subtitle -->
              <mat-form-field class="w-100 mb-2" appearance="outline">
                <mat-label>Short Description (Optional)</mat-label>
                <textarea matInput formControlName="description" rows="2"
                  placeholder="Brief summary of the milestone or lucky draw for technicians and users"></textarea>
              </mat-form-field>

              <!-- Active Status -->
              <div class="d-flex align-items-center justify-content-between p-3 bg-light rounded mt-2">
                <div>
                  <h6 class="mb-0 fw-semibold">Offer Status</h6>
                  <small class="text-muted">When active, this offer is displayed and active for registration</small>
                </div>
                <mat-slide-toggle color="primary" formControlName="isActive">
                  {{ offerForm.value.isActive ? 'Active' : 'Inactive' }}
                </mat-slide-toggle>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Section 2: Offer Type Selector -->
          <mat-card class="form-section-card mb-4">
            <mat-card-header class="pb-2">
              <mat-card-title class="fs-16 fw-bold text-primary d-flex align-items-center gap-2">
                <mat-icon>tune</mat-icon>
                <span>Offer Type & Qualification Criteria</span>
              </mat-card-title>
            </mat-card-header>
            <mat-card-content class="pt-3">
              <div class="row g-3">
                <!-- Option 1: Milestone / Category Offer -->
                <div class="col-12 col-md-6">
                  <div class="type-option-card p-3 rounded border cursor-pointer"
                    [class.active-type]="!offerForm.value.isLuckyDraw"
                    (click)="offerForm.patchValue({ isLuckyDraw: false })">
                    <div class="d-flex align-items-start gap-2">
                      <div class="type-icon-box bg-info-subtle text-info rounded p-2">
                        <mat-icon>track_changes</mat-icon>
                      </div>
                      <div>
                        <h6 class="mb-1 fw-bold">Milestone / Category Offer</h6>
                        <p class="text-muted fs-13 mb-0">
                          Set category-wise scan targets (e.g. Cat 1: 10, Cat 2: 50) and combined group targets.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Option 2: Lucky Draw -->
                <div class="col-12 col-md-6">
                  <div class="type-option-card p-3 rounded border cursor-pointer"
                    [class.active-type]="offerForm.value.isLuckyDraw"
                    (click)="offerForm.patchValue({ isLuckyDraw: true })">
                    <div class="d-flex align-items-start gap-2">
                      <div class="type-icon-box bg-warning-subtle text-warning rounded p-2">
                        <mat-icon>card_giftcard</mat-icon>
                      </div>
                      <div>
                        <h6 class="mb-1 fw-bold">Lucky Draw Entry</h6>
                        <p class="text-muted fs-13 mb-0">
                          Requires only a total scan count across any products for lucky draw eligibility.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Lucky Draw Input Box (When isLuckyDraw is True) -->
              <div *ngIf="offerForm.value.isLuckyDraw"
                class="lucky-draw-box mt-4 p-3 rounded bg-warning-subtle border border-warning">
                <div class="d-flex align-items-center gap-2 mb-2 text-warning">
                  <mat-icon>emoji_events</mat-icon>
                  <h6 class="mb-0 fw-bold">Lucky Draw Total Scan Requirement</h6>
                </div>
                <p class="text-muted fs-13 mb-3">
                  Users do not need category-specific items. Any authentic product scan will count towards this lucky
                  draw target.
                </p>
                <mat-form-field class="w-100" appearance="outline">
                  <mat-label>Total Required Scans / Quantity for Eligibility</mat-label>
                  <input matInput type="number" formControlName="luckyDrawQty" placeholder="e.g. 50" min="1" />
                  <span matSuffix class="pe-3 text-muted">scans</span>
                  <mat-hint>Users achieving this scan total within the validity dates enter the lucky draw.</mat-hint>
                </mat-form-field>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Section 3: Category Targets & Requirements (Only when NOT Lucky Draw) -->
          <mat-card *ngIf="!offerForm.value.isLuckyDraw" class="form-section-card mb-4">
            <mat-card-header class="pb-2">
              <div class="d-flex justify-content-between align-items-center w-100">
                <mat-card-title class="fs-16 fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                  <mat-icon>category</mat-icon>
                  <span>Category Targets & Breakdown</span>
                </mat-card-title>
                <div class="total-badge bg-primary text-white px-3 py-1 rounded-pill fs-13 fw-semibold">
                  Total Milestone Target: {{ calculatedMilestoneTotalQty }} pcs
                </div>
              </div>
            </mat-card-header>
            <mat-card-content class="pt-3">
              <!-- Part A: Individual Category Requirements -->
              <div class="category-section mb-4">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <h6 class="fw-bold mb-0 fs-14 text-dark">
                    <mat-icon class="inline-icon text-primary me-1">playlist_add_check</mat-icon>
                    Individual Category Targets
                  </h6>
                  <button type="button" mat-stroked-button color="primary" (click)="addCategoryRow()" class="btn-sm">
                    <mat-icon>add</mat-icon> Add Category
                  </button>
                </div>
                <p class="text-muted fs-13 mb-3">
                  Specify target quantity for each individual category (e.g. Category 1 &rarr; 10 pcs, Category 2 &rarr;
                  50 pcs).
                </p>

                <div class="category-rows-container">
                  <div *ngFor="let row of categoryRows; let i = index; trackBy: trackByIndex"
                    class="category-row-item d-flex align-items-center gap-2 mb-2 p-2 bg-light rounded">
                    <!-- Category Select -->
                    <mat-form-field class="flex-grow-1" appearance="outline" subscriptSizing="dynamic">
                      <mat-label>Select Category</mat-label>
                      <mat-select [(ngModel)]="row.categoryId" [ngModelOptions]="{ standalone: true }">
                        <mat-option *ngFor="let cat of allCategories" [value]="cat._id">
                          {{ cat.name }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>

                    <!-- Quantity Input -->
                    <mat-form-field style="width: 140px;" appearance="outline" subscriptSizing="dynamic">
                      <mat-label>Required Qty</mat-label>
                      <input matInput type="number" [(ngModel)]="row.qty" [ngModelOptions]="{ standalone: true }"
                        placeholder="10" min="1" />
                      <span matSuffix class="pe-2 text-muted">pcs</span>
                    </mat-form-field>

                    <!-- Delete Button -->
                    <button type="button" mat-icon-button color="warn" (click)="removeCategoryRow(i)"
                      matTooltip="Remove category">
                      <mat-icon>delete_outline</mat-icon>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Part B: Combined Multi-Category Targets (Custom Option) -->
              <div class="combined-category-section mt-4 pt-3 border-top">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <div>
                    <h6 class="fw-bold mb-0 fs-14 text-purple">
                      <mat-icon class="inline-icon text-purple me-1">join_inner</mat-icon>
                      Combined Multi-Category Targets (Custom Group Option)
                    </h6>
                  </div>
                  <button type="button" mat-stroked-button color="accent" (click)="addCombinedCategoryRow()"
                    class="btn-sm">
                    <mat-icon>add</mat-icon> Add Combined Group
                  </button>
                </div>
                <p class="text-muted fs-13 mb-3">
                  Group 2 or more categories together where their combined scans reach a total target (e.g. Category 4 +
                  Category 5 = 150 pcs combined).
                </p>

                <div *ngIf="combinedCategoryRows.length === 0"
                  class="empty-combined-box p-3 text-center rounded border border-dashed text-muted mb-2">
                  <small>No combined category groups added. Click "Add Combined Group" if you want combined
                    multi-category requirements.</small>
                </div>

                <div class="combined-rows-container">
                  <div *ngFor="let group of combinedCategoryRows; let j = index; trackBy: trackByIndex"
                    class="combined-group-card p-3 rounded border mb-3 bg-white">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span class="badge bg-purple text-white px-2 py-1">Combined Group #{{ j + 1 }}</span>
                      <button type="button" mat-icon-button color="warn" (click)="removeCombinedCategoryRow(j)"
                        matTooltip="Remove this combined group">
                        <mat-icon>delete_outline</mat-icon>
                      </button>
                    </div>

                    <div class="row g-2">
                      <!-- Select Multiple Categories -->
                      <div class="col-12 col-md-5">
                        <mat-form-field class="w-100" appearance="outline" subscriptSizing="dynamic">
                          <mat-label>Select Categories to Combine</mat-label>
                          <mat-select [(ngModel)]="group.categoryIds" [ngModelOptions]="{ standalone: true }" multiple>
                            <mat-option *ngFor="let cat of allCategories" [value]="cat._id">
                              {{ cat.name }}
                            </mat-option>
                          </mat-select>
                        </mat-form-field>
                      </div>

                      <!-- Group Custom Label -->
                      <div class="col-12 col-md-4">
                        <mat-form-field class="w-100" appearance="outline" subscriptSizing="dynamic">
                          <mat-label>Group Label (Optional)</mat-label>
                          <input matInput [(ngModel)]="group.groupName" [ngModelOptions]="{ standalone: true }"
                            placeholder="e.g. Premium Range Combination" />
                        </mat-form-field>
                      </div>

                      <!-- Combined Target Quantity -->
                      <div class="col-12 col-md-3">
                        <mat-form-field class="w-100" appearance="outline" subscriptSizing="dynamic">
                          <mat-label>Combined Qty</mat-label>
                          <input matInput type="number" [(ngModel)]="group.qty" [ngModelOptions]="{ standalone: true }"
                            placeholder="150" min="1" />
                          <span matSuffix class="pe-2 text-muted">pcs</span>
                        </mat-form-field>
                      </div>
                    </div>

                    <!-- Selected Categories Preview -->
                    <div *ngIf="group.categoryIds && group.categoryIds.length > 0" class="mt-2 text-muted fs-12">
                      <strong>Included Categories:</strong>
                      <span class="ms-1">{{ getCombinedCategoryNames(group.categoryIds).join(', ') }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Section 4: Rules & Guidelines -->
          <mat-card class="form-section-card mb-4">
            <mat-card-header class="pb-2">
              <div class="d-flex justify-content-between align-items-center w-100">
                <mat-card-title class="fs-16 fw-bold text-primary d-flex align-items-center gap-2 mb-0">
                  <mat-icon>gavel</mat-icon>
                  <span>Offer Rules & Guidelines</span>
                </mat-card-title>
                <button type="button" mat-stroked-button color="primary" (click)="addRule()" class="btn-sm">
                  <mat-icon>add</mat-icon> Add Rule
                </button>
              </div>
            </mat-card-header>
            <mat-card-content class="pt-3">
              <p class="text-muted fs-13 mb-3">
                Add structured individual rules. These will be formatted as bullet points in both the Mobile App and
                Admin.
              </p>

              <!-- Dynamic Rule Rows -->
              <div class="rules-container mb-3">
                <div *ngFor="let rule of rulesList; let r = index; trackBy: trackByIndex"
                  class="rule-item d-flex align-items-center gap-2 mb-2">
                  <span class="rule-index fw-bold text-muted fs-13" style="width: 24px;">#{{ r + 1 }}</span>
                  <mat-form-field class="flex-grow-1" appearance="outline" subscriptSizing="dynamic">
                    <input matInput [value]="rule" (input)="updateRule(r, $event)"
                      placeholder="e.g. Scans must be registered before validity expiry date." />
                  </mat-form-field>
                  <button type="button" mat-icon-button color="warn" (click)="removeRule(r)"
                    matTooltip="Remove this rule">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
              </div>

              <!-- General Terms & Conditions -->
              <div class="mt-3">
                <mat-form-field class="w-100" appearance="outline">
                  <mat-label>Additional Terms & Conditions / Notes</mat-label>
                  <textarea matInput formControlName="termsAndConditions" rows="3"
                    placeholder="Enter any legal disclaimers, gift claim instructions, or warranty notes..."></textarea>
                </mat-form-field>
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Right Column: Banner Image & Summary -->
        <div class="col-12 col-lg-4">
          <!-- Banner Image Card -->
          <mat-card class="form-section-card mb-4">
            <mat-card-header class="pb-2">
              <mat-card-title class="fs-16 fw-bold text-primary d-flex align-items-center gap-2">
                <mat-icon>image</mat-icon>
                <span>Offer Banner Image</span>
              </mat-card-title>
            </mat-card-header>
            <mat-card-content class="pt-3 text-center">
              <!-- Image Preview Box -->
              <div class="image-upload-dropzone p-3 rounded border text-center mb-3">
                <div *ngIf="imagePreview" class="image-preview-wrapper mb-2 position-relative">
                  <img [src]="imagePreview" alt="Offer Banner Preview"
                    class="img-fluid rounded shadow-sm offer-banner-img" />
                  <button type="button" mat-mini-fab color="warn" class="remove-image-btn" (click)="removeImage()"
                    matTooltip="Remove Image">
                    <mat-icon>delete</mat-icon>
                  </button>
                </div>

                <div *ngIf="!imagePreview" class="empty-image-placeholder py-4 text-muted">
                  <mat-icon class="upload-icon fs-36 text-muted mb-2">cloud_upload</mat-icon>
                  <p class="mb-1 fs-14 fw-medium">Upload Offer Banner</p>
                  <small class="text-muted d-block">Recommended size: 1200 &times; 600 px (JPG, PNG, WEBP)</small>
                </div>

                <button type="button" mat-stroked-button color="primary" (click)="fileInput.click()" class="mt-2">
                  <mat-icon>photo_camera</mat-icon>
                  <span>{{ imagePreview ? 'Change Banner' : 'Choose Banner Image' }}</span>
                </button>
                <input hidden (change)="onFileSelected($event)" #fileInput accept="image/jpeg,image/png,image/webp"
                  type="file" />
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Configuration Summary Box -->
          <mat-card class="form-section-card mb-4 summary-card">
            <mat-card-header class="pb-2">
              <mat-card-title class="fs-16 fw-bold text-dark d-flex align-items-center gap-2">
                <mat-icon>receipt_long</mat-icon>
                <span>Configuration Summary</span>
              </mat-card-title>
            </mat-card-header>
            <mat-card-content class="pt-2 fs-13">
              <div class="d-flex justify-content-between py-2 border-bottom">
                <span class="text-muted">Type:</span>
                <span class="fw-bold" [class.text-warning]="offerForm.value.isLuckyDraw"
                  [class.text-primary]="!offerForm.value.isLuckyDraw">
                  {{ offerForm.value.isLuckyDraw ? '🎁 Lucky Draw' : '🎯 Milestone Offer' }}
                </span>
              </div>
              <div class="d-flex justify-content-between py-2 border-bottom">
                <span class="text-muted">Display Order:</span>
                <span class="fw-bold text-dark">#{{ offerForm.value.sortOrder ?? 0 }}</span>
              </div>
              <div class="d-flex justify-content-between py-2 border-bottom">
                <span class="text-muted">Total Target Qty:</span>
                <span class="fw-bold fs-15 text-dark">{{ effectiveTotalQty }} pcs / scans</span>
              </div>
              <div *ngIf="!offerForm.value.isLuckyDraw" class="d-flex justify-content-between py-2 border-bottom">
                <span class="text-muted">Category Rules:</span>
                <span class="fw-medium">{{ categoryRows.length }} single, {{ combinedCategoryRows.length }}
                  combined</span>
              </div>
              <div class="d-flex justify-content-between py-2 border-bottom">
                <span class="text-muted">Rules Defined:</span>
                <span class="fw-medium">{{ rulesList.length }} rule item(s)</span>
              </div>
              <div class="d-flex justify-content-between py-2">
                <span class="text-muted">Status:</span>
                <span class="badge" [class.bg-success]="offerForm.value.isActive"
                  [class.bg-secondary]="!offerForm.value.isActive">
                  {{ offerForm.value.isActive ? 'Active' : 'Inactive' }}
                </span>
              </div>
            </mat-card-content>
          </mat-card>

          <!-- Form Actions Card -->
          <mat-card class="form-section-card mb-4 action-card">
            <mat-card-content class="p-3">
              <button mat-raised-button color="primary" type="submit"
                class="w-100 py-2 fs-15 fw-bold mb-2 d-flex align-items-center justify-content-center gap-2"
                [disabled]="offerForm.invalid || isSubmitting">
                <span *ngIf="!isSubmitting">{{ isAdd ? 'Create Offer' : 'Update Offer' }}</span>
                <span *ngIf="isSubmitting" class="d-flex"><mat-spinner *ngIf="isSubmitting" diameter="20"
                    class="me-2"></mat-spinner>{{ isAdd ? 'Creating...' : 'Updating...' }}</span>
              </button>

              <button mat-stroked-button type="button" class="w-100" (click)="cancel()" [disabled]="isSubmitting">
                Cancel
              </button>
            </mat-card-content>
          </mat-card>
        </div>
      </div>
    </form>
  </div>
</div>